import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon, Skeleton, Text, cn } from '@kognitos/lattice';
import {
  getFileDisplayName,
  formatMimeType,
  getInlineFileSizeBytes,
  parseFileResourceName,
} from '@/shared/utils/file-utils';
import { formatBytes, parseSizeBytes } from '@/shared/utils';
import { useFileMetadata } from '@/shared/hooks';
import type { IMetadataItem, TResolvedFile } from '../../types';
import {
  getReadableFileName,
  getValidFileExtension,
} from '../output-card-utils';

interface IMetadataSidebarItemProps {
  item: IMetadataItem;
  index: number;
  isSelected: boolean;
  onSelect: (index: number) => void;
}

/**
 * Formats array index labels by removing brackets.
 * For example: "[0]" -> "0", "[1]" -> "1"
 * Non-array labels are returned as-is.
 */
const formatArrayIndexLabel = (label: string): string => {
  const match = label.match(/^\[(\d+)\]$/);
  return match ? match[1] : label;
};

/**
 * One row of the layover's item list: a file icon, the item's name, and a
 * secondary line carrying what is known about it — extension and size for a
 * file, the value's type otherwise.
 */
export function MetadataSidebarItem({
  item,
  index,
  isSelected,
  onSelect,
}: IMetadataSidebarItemProps) {
  const { t } = useTranslation('outputs');
  const isFile = item.type === 'file';
  const fileValue = isFile ? (item.value as TResolvedFile) : null;

  // For remote files, parse the resource name and fetch metadata
  const parsedFile =
    fileValue?.type === 'file-remote'
      ? parseFileResourceName(fileValue.fileUrl)
      : null;

  // Fetch file metadata only for remote files with valid resource names
  const { data: fileMetadata, isLoading } = useFileMetadata(parsedFile);

  const remoteMetadata = parsedFile && !isLoading ? fileMetadata?.data : null;
  // A row labelled with its own resource name has no readable name yet, so it
  // waits behind a skeleton. A row that came with a name keeps it.
  const isNameLoading =
    isLoading &&
    !item.fileName &&
    fileValue?.type === 'file-remote' &&
    item.label === fileValue.fileUrl;
  const displayLabel = getReadableFileName(
    remoteMetadata
      ? getFileDisplayName(remoteMetadata.filename)
      : (item.fileName ?? item.label),
  );

  // An inline file already carries its bytes and a remote one reports them in
  // metadata, so the row can state the size without triggering a download.
  const sizeBytes = fileValue
    ? (getInlineFileSizeBytes(fileValue) ??
      parseSizeBytes(remoteMetadata?.size_bytes))
    : null;

  const mimeLabel = remoteMetadata?.mime_type
    ? formatMimeType(remoteMetadata.mime_type)
    : item.type;

  // Extension comes from the file's own name, not the row label, which need not carry one (e.g. "Invoice", "[0]").
  const extensionSource =
    fileValue?.type === 'file-inline' ? fileValue.name : displayLabel;

  // Only a file gets a second line. For anything else the type just repeats
  // what the glyph and the label already say.
  const details = isFile
    ? [
        getValidFileExtension(extensionSource).toUpperCase() || mimeLabel,
        sizeBytes === null ? '' : formatBytes(sizeBytes),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Button
      variant="ghost"
      aria-current={isSelected}
      className={cn(
        'h-auto w-full justify-start gap-3 rounded-xl px-2 py-2.5',
        isSelected ? 'bg-accent' : 'hover:bg-accent',
      )}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        onSelect(index);
      }}
    >
      {isLoading ? (
        <Skeleton
          className="size-8 shrink-0 rounded-md"
          role="status"
          aria-label={t('outputs.metadata.loading')}
        />
      ) : (
        <Flex align="center" justify="center" className="size-8 shrink-0">
          <Icon type="File" size="sm" className="text-muted-foreground" />
        </Flex>
      )}
      <Flex
        direction="column"
        align="start"
        gap={0.5}
        className="min-w-0 flex-1"
      >
        {isNameLoading ? (
          <>
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2 w-1/3" />
          </>
        ) : (
          <>
            <Text
              level="small"
              weight="medium"
              color="default"
              className="w-full truncate text-left"
              title={displayLabel}
            >
              {formatArrayIndexLabel(displayLabel)}
            </Text>
            {details && (
              <Text
                level="small"
                color="muted"
                className="w-full truncate text-left"
              >
                {details}
              </Text>
            )}
          </>
        )}
      </Flex>
    </Button>
  );
}
